import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { AlertTriangle, FileUp } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError } from '../../api/client';
import {
  applySerialImport,
  cancelSerialImport,
  createSerialImport,
  fetchProjectSerials,
  fetchSerialImport,
  resolveSerialImport,
} from '../../api/ops';
import { Modal, Segmented } from '../../ui/components';
import { formatNumber } from '../../ui/format';
import { ErrorNote } from '../admin/ErrorNote';
import './serialList.css';

type Status = api.ProjectSerial['status'];
const STATUSES: Status[] = ['not_installed', 'installed', 'elsewhere'];
const PILL: Record<Status, string> = {
  installed: 'pill-online',
  not_installed: 'pill-warning',
  elsewhere: 'pill-info',
};

/** "Client · project" of a device, or of whoever holds a serial. */
const holder = (h: { client_name: string | null; project_name: string | null } | null) =>
  h ? [h.client_name, h.project_name].filter(Boolean).join(' · ') : '';

/**
 * A project's serial list (U20): the serials with their state (installed / not installed / installed elsewhere) and,
 * for project.manage, loading a new list: a draft checked against the registry, exceptions resolved per serial, then
 * applied. The list is not applied while an exception has no resolution.
 */
export function SerialListModal({
  project,
  initialStatus = '',
  onClose,
}: {
  project: api.Project;
  initialStatus?: Status | '';
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'list' | 'load'>('list');
  return (
    <Modal
      title={t('serials.title', { name: project.name })}
      subtitle={project.client_name}
      onClose={onClose}
      actions={
        project.can_manage && (
          <Segmented
            value={tab}
            label={t('serials.serials')}
            onChange={setTab}
            options={(['list', 'load'] as const).map((k) => ({ value: k, label: t(`serials.tab.${k}`) }))}
          />
        )
      }
    >
      {tab === 'list' ? (
        <SerialTable project={project} initialStatus={initialStatus} />
      ) : (
        <SerialLoad project={project} onApplied={() => setTab('list')} />
      )}
    </Modal>
  );
}

function SerialTable({ project, initialStatus }: { project: api.Project; initialStatus: Status | '' }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status | ''>(initialStatus);
  const list = useQuery({
    queryKey: ['projectSerials', project.id],
    queryFn: () => fetchProjectSerials(project.id),
  });
  const items = list.data?.items ?? [];
  const count = (s: Status) => items.filter((i) => i.status === s).length;
  const shown = status ? items.filter((i) => i.status === status) : items;
  return (
    <div className="ops-stack">
      <div>
        <Segmented<Status | ''>
          value={status}
          label={t('common.status')}
          onChange={setStatus}
          options={[
            { value: '', label: `${t('serials.all')} (${formatNumber(items.length)})` },
            ...STATUSES.map((s) => ({
              value: s,
              label: `${t(`serials.status.${s}`)} (${formatNumber(count(s))})`,
            })),
          ]}
        />
      </div>
      <ErrorNote error={list.error} />
      <div className="ops-scroll">
        <table className="table table-tight">
          <thead>
            <tr>
              <th>{t('serials.serial')}</th>
              <th>{t('common.status')}</th>
              <th>{t('serials.device')}</th>
              <th>{t('serials.where')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.serial}>
                <td className="mono">{i.serial}</td>
                <td>
                  <span className={`pill ${PILL[i.status]}`}>{t(`serials.status.${i.status}`)}</span>
                </td>
                <td>{i.device?.name ?? '—'}</td>
                <td className="muted">
                  {i.status === 'elsewhere' ? holder(i.device) || t('serials.discovered') : ''}
                </td>
              </tr>
            ))}
            {!list.isLoading && shown.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  {items.length ? t('serials.noneFiltered') : t('serials.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SerialLoad({ project, onApplied }: { project: api.Project; onApplied: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ['serialImport', project.id];
  const draft = useQuery({
    queryKey: key,
    queryFn: () =>
      fetchSerialImport(project.id).catch((e: unknown) => {
        if (e instanceof ApiRequestError && e.status === 404) return null;
        throw e;
      }),
  });
  const [text, setText] = useState('');
  const [result, setResult] = useState<api.SerialImportResult | null>(null);
  const setDraft = (d: api.SerialImport | null) => qc.setQueryData(key, d);
  const create = useMutation({
    mutationFn: () => createSerialImport(project.id, text),
    onSuccess: (d) => {
      setDraft(d);
      setText('');
      setResult(null);
    },
  });
  const resolve = useMutation({
    mutationFn: (r: api.SerialImportResolve['resolutions']) => resolveSerialImport(project.id, r),
    onSuccess: setDraft,
  });
  const leadingZero = useMutation({
    mutationFn: (on: boolean) => resolveSerialImport(project.id, {}, on),
    onSuccess: setDraft,
  });
  const cancel = useMutation({
    mutationFn: () => cancelSerialImport(project.id),
    onSuccess: () => setDraft(null),
  });
  const apply = useMutation({
    mutationFn: () => applySerialImport(project.id),
    onSuccess: (r) => {
      setResult(r);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['projectSerials', project.id] });
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const readFile = async (f: File | undefined) => {
    if (f) setText(await f.text());
  };
  const d = draft.data;

  if (draft.isLoading) return <p className="muted">{t('common.loading')}</p>;
  if (!d)
    return (
      <div className="ops-stack">
        {result && (
          <div className="form-ok" role="status">
            {t('serials.applied', {
              listed: result.listed,
              assigned: result.assigned,
              adopted: result.adopted,
              moved: result.moved,
              notInstalled: result.not_installed,
              removed: result.removed,
            })}{' '}
            <button type="button" className="btn btn-ghost" onClick={onApplied}>
              {t('serials.seeList')}
            </button>
          </div>
        )}
        <p className="muted" style={{ margin: 0 }}>
          {t('serials.loadHint')}
        </p>
        <textarea
          className="input textarea mono serial-input"
          rows={12}
          value={text}
          placeholder={t('serials.placeholder')}
          onChange={(e) => setText(e.target.value)}
          aria-label={t('serials.serials')}
        />
        <div className="toolbar">
          <label className="btn">
            <FileUp size={15} /> {t('serials.file')}
            <input
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              hidden
              onChange={(e) => void readFile(e.target.files?.[0])}
            />
          </label>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-primary"
            disabled={!text.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {t('serials.check')}
          </button>
        </div>
        <ErrorNote error={create.error ?? draft.error} />
      </div>
    );

  const exceptions = d.items.filter((i) => i.outcome === 'other_client' || i.outcome === 'other_project');
  const of = (o: api.SerialOutcome) => d.items.filter((i) => i.outcome === o);
  return (
    <div className="ops-stack">
      <div className="kpi-row">
        <Figure label={t('serials.count.serials')} value={d.counts.serials} />
        <Figure label={t('serials.count.assign')} value={d.counts.assign} />
        <Figure label={t('serials.count.adopt')} value={d.counts.adopt} />
        <Figure label={t('serials.count.not_installed')} value={d.counts.not_installed} />
        <Figure
          label={t('serials.count.exceptions')}
          value={d.counts.exceptions}
          warn={d.counts.unresolved > 0}
        />
        <Figure label={t('serials.count.rejected')} value={d.counts.rejected} />
        <Figure label={t('serials.count.removed')} value={d.counts.removed} />
      </div>
      {d.leading_zero.short > 0 && (
        <section className="serial-section serial-zero">
          <p>{t('serials.zero.found', { short: d.leading_zero.short, matching: d.leading_zero.matching })}</p>
          <label className="check-row">
            <input
              type="checkbox"
              checked={d.leading_zero.applied}
              disabled={leadingZero.isPending}
              onChange={(e) => leadingZero.mutate(e.target.checked)}
            />
            <span>
              <strong>{t('serials.zero.add')}</strong>
              <span className="field-hint" style={{ display: 'block' }}>
                {t('serials.zero.hint')}
              </span>
            </span>
          </label>
          <ErrorNote error={leadingZero.error} />
        </section>
      )}
      {exceptions.length > 0 && (
        <section className="serial-section">
          <h4>
            <AlertTriangle size={16} /> {t('serials.exceptions', { n: exceptions.length })}
          </h4>
          <p className="muted small">{t('serials.exceptionsHint')}</p>
          <div className="ops-scroll">
            <table className="table table-wrap">
              <thead>
                <tr>
                  <th>{t('serials.serial')}</th>
                  <th>{t('serials.heldBy')}</th>
                  <th>{t('serials.device')}</th>
                  <th>{t('serials.resolution')}</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((i) => (
                  <tr key={i.serial} className={i.resolution ? '' : 'serial-unresolved'}>
                    <td className="mono nowrap">
                      {i.serial}
                      <span className="cell-sub">{t('serials.line', { n: i.line })}</span>
                    </td>
                    <td>
                      {t(`serials.outcome.${i.outcome}`)}
                      <span className="cell-sub">{holder(i.claimed_by)}</span>
                    </td>
                    <td>
                      {i.device ? i.device.name : t('serials.noDevice')}
                      <Warnings item={i} />
                    </td>
                    <td>
                      <div className="serial-choice" role="radiogroup" aria-label={t('serials.resolution')}>
                        <label className={i.can_move ? '' : 'muted'}>
                          <input
                            type="radio"
                            name={`r-${i.serial}`}
                            checked={i.resolution === 'move'}
                            disabled={!i.can_move || resolve.isPending}
                            onChange={() => resolve.mutate({ [i.serial]: 'move' })}
                          />
                          {i.outcome === 'other_client'
                            ? t('serials.moveClient', { client: project.client_name })
                            : t('serials.moveProject')}
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`r-${i.serial}`}
                            checked={i.resolution === 'skip'}
                            disabled={resolve.isPending}
                            onChange={() => resolve.mutate({ [i.serial]: 'skip' })}
                          />
                          {t('serials.skip')}
                        </label>
                        {!i.can_move && <span className="muted small">{t('serials.cannotMove')}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ErrorNote error={resolve.error} />
        </section>
      )}
      <Details title={t('serials.outcome.adopt')} n={of('adopt').length}>
        <ItemTable items={of('adopt')} withWarnings />
      </Details>
      <Details title={t('serials.outcome.assign')} n={of('assign').length}>
        <ItemTable items={of('assign')} />
      </Details>
      <Details title={t('serials.outcome.not_installed')} n={of('not_installed').length}>
        <ItemTable items={of('not_installed')} />
      </Details>
      <Details title={t('serials.outcome.unchanged')} n={of('unchanged').length}>
        <ItemTable items={of('unchanged')} />
      </Details>
      <Details title={t('serials.removed')} n={d.removed.length} hint={t('serials.removedHint')}>
        <table className="table table-tight">
          <tbody>
            {d.removed.map((r, k) => (
              <tr key={r.serial ?? r.device?.id ?? k}>
                <td className="mono">{r.serial ?? '—'}</td>
                <td>{r.device?.name ?? t('serials.noDevice')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Details>
      <Details title={t('serials.rejected')} n={d.rejected.length} open={d.rejected.length > 0}>
        <table className="table table-tight">
          <tbody>
            {d.rejected.map((r) => (
              <tr key={`${r.line}-${r.raw}`}>
                <td className="nowrap muted">{t('serials.line', { n: r.line })}</td>
                <td className="mono">{r.raw}</td>
                <td>{t(`serials.reason.${r.reason}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Details>
      <ErrorNote error={apply.error ?? cancel.error} />
      <div className="toolbar">
        <button
          type="button"
          className="btn btn-ghost"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {t('serials.discard')}
        </button>
        <span style={{ flex: 1 }} />
        {d.counts.unresolved > 0 && (
          <span className="muted small">{t('serials.unresolved', { n: d.counts.unresolved })}</span>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={d.counts.unresolved > 0 || apply.isPending}
          onClick={() => apply.mutate()}
        >
          {t('serials.apply')}
        </button>
      </div>
    </div>
  );
}

function Figure({ label, value, warn = false }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={`card kpi${warn ? ' serial-warn' : ''}`}>
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{formatNumber(value)}</span>
    </div>
  );
}

function Warnings({ item }: { item: api.SerialImportItem }) {
  const { t } = useTranslation();
  if (!item.warnings.length && !item.position_client) return null;
  return (
    <span className="cell-sub">
      {item.warnings.map((w) => t(`serials.warning.${w}`)).join(' · ')}
      {item.position_client &&
        (item.warnings.length ? ' · ' : '') + t('serials.positionIn', { client: item.position_client })}
    </span>
  );
}

function Details({
  title,
  n,
  hint,
  open = false,
  children,
}: {
  title: string;
  n: number;
  hint?: string;
  open?: boolean;
  children: ReactNode;
}) {
  if (!n) return null;
  return (
    <details className="serial-section" open={open}>
      <summary>
        <strong>{title}</strong> <span className="muted">({formatNumber(n)})</span>
      </summary>
      {hint && <p className="muted small">{hint}</p>}
      <div className="ops-scroll">{children}</div>
    </details>
  );
}

function ItemTable({
  items,
  withWarnings = false,
}: {
  items: api.SerialImportItem[];
  withWarnings?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <table className="table table-tight">
      <tbody>
        {items.map((i) => (
          <tr key={i.serial}>
            <td className="mono nowrap">{i.serial}</td>
            <td>
              {i.device?.name ?? t('serials.noDevice')}
              {withWarnings && <Warnings item={i} />}
            </td>
            <td className="muted">{i.device ? holder(i.device) || t('serials.discovered') : ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
